// Sign-in screen checks and copy (v1.3 ticket 09, RCMC-13 / RCMC-14).
//
// One place for what each form checks before it sends anything, and for turning a server refusal into an error on
// the field it concerns. Every check returns `{ <field name>: <error text> }` for the fields in error, in the
// form's order (the screen focuses the first and announces them all); `{}` means nothing to report.
//
// Copy = owner rulings of 12 Oct 2026 (ticket 09): the OTP line is channel-neutral because the OTP goes by email
// today while SMS may come back; errors follow the Q26 style ("Enter your …", em dash, no exclamation marks). The
// rules themselves are v1.2's, except that a new password on Reset password now needs 10 characters, as on
// Create Account (v1.2 accepted any, even an empty one).
import { hasLength, hasMinimumLength, isEmail, isEmpty, isInteger } from "../Util/validation.js";

// Status messages (announced in the status region)
export const OTP_SENT = "OTP sent — check your registered email ID or phone.";
export const RESEND_READY = "You can now resend the OTP.";
export const ACCOUNT_CREATED = "Account created — sign in with your phone number and password.";
export const PASSWORD_RESET = "Password reset — sign in with your new password.";
// Ticket 10 (RCMC-22): shown on arrival when the session ended (expired or refused) — never after a Sign out
export const SESSION_EXPIRED = "Your session has expired. Sign in again to continue.";

// Field errors (next to the field and in the alert region)
const ENTER_NAME = "Enter your name.";
const ENTER_PAN = "Enter your PAN No.";
const ENTER_EMAIL = "Enter a valid email address, like name@example.com.";
const ENTER_PHONE = "Enter your 10-digit phone number.";
const ENTER_PASSWORD = "Enter your password.";
const CHOOSE_PASSWORD = "Choose a password of at least 10 characters.";
const PASSWORDS_DIFFER = "The passwords do not match — enter the same password twice.";
const ENTER_OTP = "Enter the 6-digit OTP.";
const UNKNOWN_PHONE = "No account uses this phone number — check it, or create an account.";
const WRONG_PASSWORD = "Incorrect password — try again, or use Forgot Password.";
const PHONE_TAKEN = "An account with this phone number already exists — sign in instead.";

// Create Account's fields, in form order: also the register API's field names
export const REGISTER_FIELDS = ["name", "pan_no", "email", "phone_no", "password", "re_enter_password"];

const isPhoneNo = (value = "") => !isEmpty(value) && hasLength(value, 10) && isInteger(value);
const isOtp = (value = "") => hasLength(value, 6) && isInteger(value);
const isNewPassword = (value = "") => !isEmpty(value) && hasMinimumLength(value, 10);

/** Keep only the fields that failed: [[name, error or ""], …] → { name: error } in the same order */
const failed = (pairs) => Object.fromEntries(pairs.filter(([, error]) => error));

/** Sign in, first step: phone number + password */
export function checkSignIn({ phone_no, password = "" }) {
  return failed([
    ["phone_no", isPhoneNo(phone_no) ? "" : ENTER_PHONE],
    ["password", isEmpty(password) ? ENTER_PASSWORD : ""],
  ]);
}

/** Create Account, first step: every field */
export function checkRegister({ name = "", pan_no = "", email = "", phone_no, password, re_enter_password }) {
  return failed([
    ["name", isEmpty(name) ? ENTER_NAME : ""],
    ["pan_no", isEmpty(pan_no) ? ENTER_PAN : ""],
    ["email", isEmpty(email) || !isEmail(email) ? ENTER_EMAIL : ""],
    ["phone_no", isPhoneNo(phone_no) ? "" : ENTER_PHONE],
    ["password", isNewPassword(password) ? "" : CHOOSE_PASSWORD],
    ["re_enter_password", password === re_enter_password ? "" : PASSWORDS_DIFFER],
  ]);
}

/** Reset password, first step: the phone number the OTP goes to */
export function checkResetPhone({ phone_no }) {
  return failed([["phone_no", isPhoneNo(phone_no) ? "" : ENTER_PHONE]]);
}

/** Reset password, second step: OTP + the new password twice */
export function checkReset({ otp, password, re_enter_password }) {
  return failed([
    ["otp", isOtp(otp) ? "" : ENTER_OTP],
    ["password", isNewPassword(password) ? "" : CHOOSE_PASSWORD],
    ["re_enter_password", password === re_enter_password ? "" : PASSWORDS_DIFFER],
  ]);
}

/** The OTP step of Sign in and Create Account */
export function checkOtp(otp) {
  return failed([["otp", isOtp(otp) ? "" : ENTER_OTP]]);
}

// Server refusals → field errors. Anything not mapped here returns {} and the screen announces errorMessage()
// instead (network failure, 5xx, throttling, …).

/** Sign in refused (UserPortal/views.py Login): 404 = no account with that phone, 400 = wrong password */
export function signInRefusal(error) {
  const status = error?.response?.status;
  if (status === 404) return { phone_no: UNKNOWN_PHONE };
  if (status === 400) return { password: WRONG_PASSWORD };
  return {};
}

/** Reset password's OTP request refused (VerifyUser): 400 = no account with that phone */
export function resetPhoneRefusal(error) {
  return error?.response?.status === 400 ? { phone_no: UNKNOWN_PHONE } : {};
}

/**
 * Create Account refused (RegisterUser): the message is the serializer's errors, `{ field: [text, …] }` — v1.2
 * passed that object to alert() and showed "[object Object]". The one refusal an Investor can cause through the
 * form is a phone number that already has an account (the field is unique); any other field error is shown as
 * the server words it.
 */
export function registerRefusal(error) {
  const message = error?.response?.data?.message;
  if (!message || typeof message !== "object") return {};
  return failed(
    REGISTER_FIELDS.map((name) => {
      const text = Array.isArray(message[name]) ? String(message[name][0] ?? "") : "";
      if (name === "phone_no" && /already exists/i.test(text)) return [name, PHONE_TAKEN];
      return [name, text];
    })
  );
}

/** An OTP refused by a verify endpoint (400: wrong, expired): the server's own text, on the OTP field */
export function otpRefusal(error) {
  const message = error?.response?.data?.message;
  return error?.response?.status === 400 && typeof message === "string" && !isEmpty(message) ? { otp: message } : {};
}
